import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import type { MouseEvent } from 'react';
import { Link } from 'wouter';
import { Heart, UserRound, HeartHandshake, User, ChevronRight } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { useSeo } from '@/lib/useSeo';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { collectionUrl } from '@/lib/collectionUrl';

/* ─── Design tokens ────────────────────────────────────────── */
const DARK   = '#2A1F14';
const GOLD   = '#e05590';
const MUTED  = 'rgba(42,31,20,0.50)';
const CREAM  = '#FAF7F2';
const CREAM2 = '#F5EFE6';
const LINE   = 'rgba(210,60,120,0.18)';

const DLA_KOGO_ITEMS = [
  { label: 'Dla Pary Młodej', href: collectionUrl('Dla-pary-mlodej'), icon: Heart,         desc: 'Eleganckie bukiety i kompozycje ślubne na najważniejszy dzień. Piękna oprawa wyjątkowej chwili dla nowożeńców.' },
  { label: 'Dla mamy',        href: collectionUrl('Dla-mamy'),        icon: UserRound,     desc: 'Wyjątkowe kwiaty dla mamy - bo mama zasługuje na to, co najpiękniejsze. Wyraź miłość i wdzięczność.' },
  { label: 'Dla żony',        href: collectionUrl('Dla-zony'),        icon: Heart,         desc: 'Romantyczne bukiety dla żony na co dzień i od święta. Miłość, która rozkwita razem z każdym bukietem.' },
  { label: 'Dla dziewczyny',  href: collectionUrl('Dla-dziewczyny'), icon: HeartHandshake,desc: 'Romantyczne gesty i piękne bukiety, które zachwycają. Dostawa kwiatów w Lublinie tego samego dnia.' },
  { label: 'Dla babci',       href: collectionUrl('Dla-babci'),       icon: UserRound,     desc: 'Ciepłe i piękne kwiaty dla babci - wdzięczność i miłość w każdym płatku. Niespodzianka, która wzruszy.' },
  { label: 'Dla mężczyzny',   href: collectionUrl('Dla-mezczyzny'),  icon: User,          desc: 'Eleganckie i efektowne kompozycje kwiatowe dla mężczyzny - nowoczesne i bez zbędnych ozdób.' },
  { label: 'Dla przyjaciela', href: collectionUrl('Dla-przyjaciela'),icon: UserRound,     desc: 'Gest przyjaźni i życzliwości - kwiaty dla przyjaciela lub przyjaciółki na każdą chwilę.' },
];

export default function DlaKogoHub() {
  usePrerenderReady();
  
  useSeo({
    title: 'Kwiaty dla każdego — znajdź idealny bukiet dla bliskich',
    description: 'Wybierz bukiet idealnie dopasowany do osoby, którą chcesz obdarować. Oferujemy piękne kwiaty dla mamy, żony, dziewczyny i nie tylko. Dostawa w Lublinie.',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      
      {/* Hero ────────────────────────────────────────────────────── */}
      <section style={{
        background: '#fff',
        position: 'relative', overflow: 'hidden',
        borderBottom: `1px solid ${LINE}`,
      }}>
        <PageBreadcrumb crumbs={[{ label: 'Dla kogo' }]} />

        <div style={{
          maxWidth: 1370, margin: '0 auto',
          padding: 'max(clamp(24px,3vw,44px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(20px,2.5vw,32px)',
          marginTop: 40
        }}>
          <h1 className="bk-fade-up bk-d1"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(36px,5vw,64px)', fontWeight: 600, lineHeight: 1.1, color: DARK, margin: '0 0 20px', letterSpacing: '-0.01em' }}><CmsEditorialText id="editorial.DlaKogoHub.h1.1" value={"Kwiaty"} section="Wstęp i opisy" label="Nagłówek 1" /><br />
            <span style={{ color: GOLD, fontStyle: 'italic' }}>dla wyjątkowej osoby</span>
          </h1>

          <p className="bk-fade-up bk-d2"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, lineHeight: 1.7, color: MUTED, maxWidth: 540, marginBottom: 32 }}><CmsEditorialText id="editorial.DlaKogoHub.p.1" value={"Każdy zasługuje na coś pięknego. Znajdź kompozycję idealnie dopasowaną do osoby, którą chcesz obdarować. Spraw, by poczuła się naprawdę wyjątkowo."} section="Wstęp i opisy" label="Akapit 1" multiline /></p>
        </div>
      </section>

      {/* Siatka Osób ──────────────────────────────────────────── */}
      <section style={{ flexGrow: 1, padding: 'clamp(40px, 6vw, 80px) 0' }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          
          <div style={{ marginBottom: 32 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: GOLD, margin: '0 0 12px' }}><CmsEditorialText id="editorial.DlaKogoHub.p.2" value={"Dla kogo"} section="Wstęp i opisy" label="Akapit 2" multiline /></p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 600, color: DARK, margin: 0, letterSpacing: '0.01em' }}><CmsEditorialText id="editorial.DlaKogoHub.h2.1" value={"Wybierz osobę"} section="Wstęp i opisy" label="Nagłówek 1" /></h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
            gap: 16,
          }}>
            {DLA_KOGO_ITEMS.map(item => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    padding: '20px 24px',
                    background: '#fff',
                    border: `1px solid ${LINE}`,
                    borderRadius: 12,
                    textDecoration: 'none',
                    transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.borderColor = GOLD;
                    el.style.boxShadow = `0 6px 20px rgba(210,60,120,0.08)`;
                    el.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.borderColor = LINE;
                    el.style.boxShadow = 'none';
                    el.style.transform = 'translateY(0)';
                  }}
                >
                  {/* nagłówek karty */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      width: 40, height: 40,
                      borderRadius: 10,
                      background: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={18} color={GOLD} strokeWidth={1.6} />
                    </span>

                    <span style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 16,
                      fontWeight: 600,
                      color: DARK,
                      flexGrow: 1,
                      lineHeight: 1.3,
                    }}>
                      {item.label}
                    </span>

                    <ChevronRight size={16} color={MUTED} strokeWidth={1.6} style={{ flexShrink: 0 }} />
                  </div>

                  {/* opis */}
                  <p style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 13.5,
                    color: MUTED,
                    lineHeight: 1.65,
                    margin: 0,
                    paddingLeft: 52, // 40 (icon) + 12 (gap)
                  }}>
                    {item.desc}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
